const API_BASE_URL = 'https://mmstudio.co.kr:10444';

// 공통 fetch 헬퍼
const apiRequest = async (url, options = {}) => {
  const response = await fetch(url, { headers: { 'accept': '*/*' }, ...options });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// 마지막 거래일 가져오기
export const getLastRunDate = async (ticker) => {
  try {
    const data = await apiRequest(`${API_BASE_URL}/api/v1/open/stock-detail/${ticker}/run-date`);
    return data.runDate;
  } catch (error) {
    console.error('Error fetching last run date:', error);
    return new Date().toISOString().split('T')[0];
  }
};

const getRunDate = () => {
  // return '2025-07-18'; // Fixed date for now 2
  const today = new Date().toISOString().split('T')[0];
  return today; // Use today's date
};

export const getInvestmentRecommendation = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/summary-report/${ticker}/investment-recommendation?runDate=${runDate}`);

export const getNewsAnalysis = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/summary-report/${ticker}/news?runDate=${runDate}`);

export const getRiskAnalysis = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/summary-report/${ticker}/risk?runDate=${runDate}`);

export const getSentimentAnalysis = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/summary-report/${ticker}/sentiment?runDate=${runDate}`);

export const getTechnicalAnalysis = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/summary-report/${ticker}/technical?runDate=${runDate}`);

export const getFundamentalAnalysis = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/summary-report/${ticker}/fundamental?runDate=${runDate}`);

export const getStockDetailFundamental = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/stock-detail/${ticker}/fundamental?runDate=${runDate}`);

export const getStockDetailTechnical = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/stock-detail/${ticker}/technical?runDate=${runDate}`);

export const getStockDetailNews = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/stock-detail/${ticker}/news?runDate=${runDate}`);

export const getStockDetailSummary = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/stock-detail/${ticker}/summary?runDate=${runDate}`);

export const getInvestmentReport = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/report/${ticker}/investment-recommendation?runDate=${runDate}`);

export const getNewsReport = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/report/${ticker}/news?runDate=${runDate}`);

export const getTechnicalReport = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/report/${ticker}/technical?runDate=${runDate}`);

export const getFundamentalReport = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/report/${ticker}/fundamental?runDate=${runDate}`);

export const getSentimentReport = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/report/${ticker}/sentiment?runDate=${runDate}`);

export const getRiskReport = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/report/${ticker}/risk?runDate=${runDate}`);

export const getDashboardMainContent = () =>
  apiRequest(`${API_BASE_URL}/api/v1/open/dashboard/dashboard-main-content`);

export const getDashboardMarketData = () =>
  apiRequest(`${API_BASE_URL}/api/v1/open/dashboard/dashboard-market-data`);

export const getSearchTickers = () =>
  apiRequest(`${API_BASE_URL}/api/v1/open/search/tickers`);

export const searchStocks = (query) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/search?query=${encodeURIComponent(query)}`);

export const getStockDetailCommunity = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/stock-detail/${ticker}/community?runDate=${runDate}`);

export const getShortPositionAnalysis = (ticker, runDate) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/analysis/short-position/${ticker}?runDate=${runDate}`);

// 임시 로그인으로 토큰 발급
export const loginTemp = (username) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/auth/login-temp`, {
    method: 'POST',
    headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
    body: JSON.stringify({ username }),
  });

// 즐겨찾기 목록 가져오기
export const getFavoriteList = (accessToken) =>
  apiRequest(`${API_BASE_URL}/api/v1/auth/favorite`, {
    headers: { 'accept': '*/*', 'Authorization': `Bearer ${accessToken}` },
  });

// 즐겨찾기 추가/삭제 (통합 API)
export const toggleFavorite = (accessToken, ticker, isFavorite) =>
  apiRequest(`${API_BASE_URL}/api/v1/auth/favorite`, {
    method: 'PUT',
    headers: {
      'accept': '*/*',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ ticker, isFavorite }),
  });

// 하위 호환성을 위한 래퍼 함수들
export const addFavorite = async (accessToken, ticker) => {
  return await toggleFavorite(accessToken, ticker, true);
};

export const deleteFavorite = async (accessToken, ticker) => {
  return await toggleFavorite(accessToken, ticker, false);
};

// ===== Community APIs =====

// localStorage에서 토큰 가져오기
export const getAccessToken = () => {
  try {
    const tokenInfo = localStorage.getItem('tokenInfo');
    if (tokenInfo) {
      const parsedTokenInfo = JSON.parse(tokenInfo);
      // 토큰 만료 시간 체크
      if (parsedTokenInfo.expireTime && Date.now() < parsedTokenInfo.expireTime) {
        return parsedTokenInfo.accessToken;
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting access token:', error);
    return null;
  }
};

// 커뮤니티 게시글 목록 가져오기
export const getCommunityPosts = (cursor = null) => {
  const url = cursor
    ? `${API_BASE_URL}/api/v1/open/community?cursor=${cursor}`
    : `${API_BASE_URL}/api/v1/open/community`;
  return apiRequest(url);
};

// 댓글 목록 가져오기
export const getCommunityReplies = (communityNo) =>
  apiRequest(`${API_BASE_URL}/api/v1/open/community/reply?communityNo=${communityNo}`);

export const getUserInfo = () => {
  const token = getAccessToken();
  if (!token) throw new Error('No access token available');
  return apiRequest(`${API_BASE_URL}/api/v1/user`, {
    headers: { 'accept': '*/*', 'Authorization': `Bearer ${token}` },
  });
};

// 게시글/댓글 작성
export const submitCommunityPost = (content, parentCommunityNo = null) => {
  const token = getAccessToken();
  if (!token) throw new Error('No access token available');
  return apiRequest(`${API_BASE_URL}/api/v1/auth/community`, {
    method: 'POST',
    headers: {
      'accept': '*/*',
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content: content.trim(), parentCommunityNo: parentCommunityNo || 0 }),
  });
};

// X 트윗 데이터 가져오기
export const getXTweetData = () =>
  apiRequest(`${API_BASE_URL}/api/v1/open/dashboard/x`);

// 공포 탐욕 지수 가져오기
export const getFearGreedIndex = () =>
  apiRequest(`${API_BASE_URL}/api/v1/open/dashboard/fear-and-greed-index`);

// 주식 랭킹 가져오기
export const getStockRanking = () =>
  apiRequest(`${API_BASE_URL}/api/v1/open/dashboard/stock-ranking`);